<template>
  <button class="btn btn-primary" @click="openModal">
    <i class="fa-solid fa-plus"></i> Tạo mới
  </button>
  <dialog ref="dialogRef" class="modal">
    <div class="modal-box max-w-2xl">
      <h3 class="mb-4 text-xl font-bold">Tạo mới</h3>
      <div class="flex flex-wrap">
        <div class="grow p-4 pt-0">
          <fieldset class="fieldset">
            <legend class="fieldset-legend text-base">Tên</legend>
            <input
              v-model.trim="name"
              type="text"
              class="input input-bordered w-full"
              placeholder="Nhập tên"
            />
            <label class="fieldset-label">
              Tên này sẽ được sử dụng để xác định phiên bản.
            </label>
          </fieldset>

          <fieldset class="fieldset text-base">
            <legend class="fieldset-legend">Loại</legend>
            <select v-model="reconType" class="select select-bordered w-full">
              <option value="recon">Recon</option>
              <option value="password_detection">Password Detection</option>
              <option value="bruteforce">Bruteforce</option>
              <option value="spread_lan">Spread LAN</option>
            </select>
          </fieldset>

          <fieldset class="fieldset text-base">
            <legend class="fieldset-legend">Mô tả</legend>
            <textarea
              v-model.trim="description"
              class="textarea textarea-bordered w-full"
              placeholder="Nhập mô tả"
            ></textarea>
          </fieldset>
        </div>

        <div class="grow p-4 pt-0">
          <legend class="fieldset-legend">Tải lên file</legend>
          <div class="flex w-full gap-4">
            <div class="w-full">
              <label
                for="fileInput"
                :class="`flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-all duration-300 ease-in-out hover:border-gray-600 hover:bg-gray-100 ${isDraggingOver ? 'border-green-500 bg-green-500/10' : ''}`"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
              >
                <input
                  type="file"
                  id="fileInput"
                  ref="fileInput"
                  @change="handleFileChange"
                  hidden
                />
                <p class="text-base-content/60 m-0 text-sm" v-if="!file">
                  Kéo và thả tệp vào đây hoặc
                  <span class="text-primary font-bold underline">chọn tệp</span>
                </p>
                <p class="text-base-content/60 m-0 text-sm" v-else>
                  Tệp đã chọn: {{ file.name }}
                </p>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="px-4 pb-4" v-if="isUploading">
        <div class="mb-1 flex justify-between">
          <span class="text-sm font-medium">{{
            uploadProgress === 100
              ? "Đang chờ phản hồi từ máy chủ..."
              : "Đang tải lên"
          }}</span>
          <span class="text-sm font-medium">{{ uploadProgress }}%</span>
        </div>
        <progress
          class="progress progress-primary w-full"
          :value="uploadProgress"
          max="100"
        ></progress>
      </div>
      <div class="modal-action">
        <button class="btn" @click="closeModal">Đóng</button>
        <button
          :disabled="!file || !name.trim() || !reconType || isUploading"
          @click="handleSave"
          class="btn btn-primary"
        >
          <span v-if="isUploading">
            <span class="loading loading-spinner text-white"></span>
          </span>
          <span v-else>Tạo</span>
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { axios_instance } from "@/api/api";
import { useQueryClient } from "@tanstack/vue-query";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import type { version } from "./index";

const queryClient = useQueryClient();

const dialogRef = ref<HTMLDialogElement | null>(null);
const file = ref<File | null>(null);
const isDraggingOver = ref(false);
const name = ref("");
const reconType = ref("recon");
const description = ref("");
const uploadProgress = ref(0);
const isUploading = ref(false);

const openModal = () => {
  dialogRef.value?.showModal();
};

const closeModal = () => {
  dialogRef.value?.close();
  // Reset fields on close
  name.value = "";
  reconType.value = "recon";
  description.value = "";
  file.value = null;
  uploadProgress.value = 0;
};

const handleSave = async () => {
  try {
    if (!file.value) {
      toast.error("Thiếu file để tải lên");
      return;
    }

    const checkData = JSON.stringify({
      keyword: name.value,
      page: 1,
      size: 50,
      type: [reconType.value],
    });

    const checkConfig = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/version/pagination",
      data: checkData,
    };

    const checkResponse = await axios_instance.request<{
      data: version[] | null;
      total_filter: number;
    }>(checkConfig);

    const isExist = checkResponse.data?.data?.some(
      (item) => item.version === name.value,
    );

    if (isExist) {
      toast.error("Phiên bản đã tồn tại!");
      return;
    }

    const data = new FormData();
    data.append("type", reconType.value);
    data.append("version", name.value);
    data.append("description", description.value);
    data.append("file", file.value);

    isUploading.value = true;
    uploadProgress.value = 0;

    await axios_instance.post("/api/v1/view/version/", data, {
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          uploadProgress.value = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
        }
      },
    });

    toast.success("Tạo thành công!");
    queryClient.invalidateQueries({ queryKey: ["recon"] });
    closeModal();
  } catch (error) {
    console.log(error);
    toast.error("Đã xảy ra lỗi khi tạo.");
  } finally {
    isUploading.value = false;
    uploadProgress.value = 0;
  }
};

const handleDragOver = () => {
  isDraggingOver.value = true;
};

const handleDragLeave = () => {
  isDraggingOver.value = false;
};

const handleDrop = (event: DragEvent) => {
  isDraggingOver.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    file.value = files[0] || null;
  }
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files && files.length > 0) {
    file.value = files[0] || null;
  }
};
</script>
